import { useMemo, useState } from "react";
import { Box } from "../../components/Box";
import { ExpansionPanel } from "../../components/ExpansionPanel";
import { Flex } from "../../components/Flex";
import { H3 } from "../../components/Heading";
import { Image } from "../../components/Image";
import { Loader } from "../../components/Loader";
import { Paragraph } from "../../components/Paragraph";
import { Slider } from "../../components/Slider";
import styled from "styled-components";
import {
  CheckStatusEnum,
  RiskLevelColors,
  RiskLevelEnum,
  RiskLevelIcons,
} from "../../context/MandateStatusContext";
import { useIndividualCheck } from "../../data/useIndividualCheck";
import { capitalise } from "../../utils/string";
import {
  CheckContainer,
  CheckHeading,
  CheckParagraph,
  CheckTitle,
  Section,
} from "./CheckStyledComponents";
import { ApprovalComment } from "./approvalComment";
import { CheckFields } from "./CheckFields";
import { CheckResults } from "./CheckResults";
import { IndividualChecksMap } from "./helper";
import { ManualAcceptReject } from "./ManualAcceptReject";

const riskRank = {
  low: 1,
  medium: 2,
  high: 3,
};

const SummaryValue = styled(Paragraph)`
  ${({ theme: { colors, fontSizes } }: any) => `
    color: ${colors.gray[60]};
    font-size: ${fontSizes[1]};
    margin-bottom: 0;
    margin-top: 0;
  `}
`;

const getRiskHighlight = (risk?: string) => {
  if (risk === "high") return "#FFF4D4";
  if (risk === "medium") return "#FFF9E8";
  return "";
};

const getRiskAccent = (risk?: string) => {
  if (risk === "high") return "#F5DCA2";
  if (risk === "medium") return "#F8E7BD";
  return "";
};

const PartyPanel = styled.div<{ risk?: string; expanded?: boolean }>`
  ${({ risk, expanded, theme: { colors, radii, space } }: any) => {
    const highlight = getRiskHighlight(risk);
    const accent = getRiskAccent(risk);

    return `
    background: ${expanded ? colors.white : highlight || colors.white};
    border: 1px solid ${highlight || colors.gray[10]};
    border-left: ${
      accent ? `4px solid ${accent}` : `1px solid ${colors.gray[10]}`
    };
    border-radius: ${radii[1]};
    margin-bottom: ${space[2]};
    padding: ${space[3]};
  `;
  }}
`;

const PanelTitleGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 2fr) minmax(90px, 0.7fr) minmax(
      150px,
      1.4fr
    ) minmax(90px, 0.7fr) minmax(120px, 0.9fr);
  gap: 16px;
  align-items: center;

  @media screen and (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 16px;
`;

const DetailSection = styled.div`
  ${({ theme: { colors, space } }: any) => `
    border-top: 1px solid ${colors.gray[10]};
    padding-top: ${space[2]};

    &:first-child {
      border-top: none;
      padding-top: 0;
    }
  `}
`;

const DetailSectionTitle = styled(H3)`
  ${({ theme: { fontSizes, space } }: any) => `
    font-size: ${fontSizes[2]};
    margin-bottom: ${space[2]};
  `}
`;

const DetailItem = styled.div<{ wide?: boolean }>`
  ${({ wide }: any) => (wide ? "grid-column: 1 / -1;" : "")}
`;

const DetailLabel = styled(Paragraph)`
  ${({ theme: { colors, fontSizes } }: any) => `
    color: ${colors.gray[50]};
    font-size: ${fontSizes[0]};
    font-weight: 600;
    margin: 0 0 4px;
  `}
`;

const DetailValue = styled(Paragraph)`
  ${({ theme: { colors, fontSizes } }: any) => `
    color: ${colors.gray[70]};
    font-size: ${fontSizes[1]};
    margin: 0;
    overflow-wrap: anywhere;
  `}
`;

const normalizeRisk = (risk?: string | null) => {
  const value = risk?.toLowerCase();
  return value === "high" || value === "medium" || value === "low" ? value : "";
};

const getDisplayValue = (value: any) => {
  if (value === undefined || value === null || value === "") return "-";
  if (typeof value === "object") {
    const next =
      value.name ??
      value.label ??
      value.status ??
      value.title ??
      value.id;

    return typeof next === "object" ? getDisplayValue(next) : next ?? "-";
  }
  return String(value);
};

const isEntityParty = (party: any) =>
  String(party?.type ?? party?._type ?? party?.profile?._type ?? "")
    .toLowerCase()
    .includes("entity");

const getParties = (instance: any) => {
  const parties =
    instance?.associated_parties ??
    instance?.parties ??
    instance?.party_snapshot ??
    instance?.snapshot?.parties ??
    instance?.results ??
    [];

  return Array.isArray(parties) ? parties : [];
};

const getPartyName = (party: any) =>
  party?.name ||
  party?.party_name ||
  party?.profile?.name ||
  [party?.first_name, party?.middle_name, party?.last_name]
    .filter(Boolean)
    .join(" ") ||
  "-";

const getPartyType = (party: any) =>
  capitalise(
    getDisplayValue(
      party?.party_type ?? party?._type ?? party?.type ?? party?.profile?._type,
    ),
  );

const getRelationshipType = (party: any) =>
  getDisplayValue(
    party?.relationship_type ??
      party?.relationship ??
      party?.relation_type ??
      party?.relationshipType,
  );

const getEntityType = (party: any) =>
  isEntityParty(party)
    ? getDisplayValue(party?.entity_type ?? party?.profile?.entity_type)
    : "-";

const getOwnership = (party: any) => {
  const value =
    party?.ownership_percentage ??
    party?.ownership ??
    party?.shareholding_percentage ??
    party?.percentage;

  if (value === undefined || value === null || value === "") return "-";
  return String(value).includes("%") ? String(value) : `${value}%`;
};

const DateFormatter = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-GB");
};

const formatStatus = (value?: any) => {
  const displayValue = getDisplayValue(value);
  return displayValue === "-" ? "-" : capitalise(String(displayValue));
};

const getAddressSummary = (party: any) => {
  const address = Array.isArray(party?.addresses) ? party.addresses[0] : null;
  if (!address) return "";

  const summary = [
    address?.address_type,
    address?.post_zip_code,
    address?.country_code,
  ]
    .filter(Boolean)
    .join(" - ");

  if (summary) return summary;

  return [address?.address_type, address?.post_zip_code, address?.country_code]
    .filter(Boolean)
    .join(" · ");
};

const getAddressLines = (party: any) => {
  const address = Array.isArray(party?.addresses) ? party.addresses[0] : null;
  if (!address) return [];

  const firstLine = [
    address?.flat_appartment_subbuilding,
    address?.building_number,
    address?.building_name,
    address?.road_street,
  ]
    .filter(Boolean)
    .join(" ");

  const secondLine = [address?.town_city, address?.district]
    .filter(Boolean)
    .join(", ");

  return [
    address?.address_type,
    firstLine,
    secondLine,
    address?.state_province_name,
    address?.post_zip_code,
    address?.country_code,
  ].filter(Boolean);
};

const getAddressLine = (party: any) => getAddressLines(party).join(", ");

const getEntityTypeRisk = (party: any) =>
  isEntityParty(party)
    ? normalizeRisk(
        party?.entity_type_risk_level ??
          party?.entity_type_risk ??
          party?.risk?.entity_type ??
          party?.risk_assessment?.entity_type_risk,
      )
    : "";

const getRelationshipRisk = (party: any) =>
  normalizeRisk(
    party?.relationship_type_risk_level ??
      party?.relationship_type_risk ??
      party?.relationship_risk ??
      party?.risk?.relationship_type ??
      party?.risk_assessment?.relationship_type_risk,
  );

const getHighestPartyRisk = (party: any) => {
  const explicit = normalizeRisk(
    party?.highest_risk_level ?? party?.risk_level ?? party?.risk,
  );
  if (explicit) return explicit;

  const entityRisk = getEntityTypeRisk(party);
  const relationshipRisk = getRelationshipRisk(party);
  return (
    [entityRisk, relationshipRisk]
      .filter(Boolean)
      .sort((a, b) => riskRank[b] - riskRank[a])[0] ?? "low"
  );
};

const RiskBadge = ({ risk }: { risk?: string }) => {
  const level = normalizeRisk(risk);

  if (!level) {
    return <CheckParagraph>-</CheckParagraph>;
  }

  return (
    <Flex gap="8" alignItems="center">
      <CheckParagraph color={RiskLevelColors[level]}>
        {capitalise(RiskLevelEnum[level])}
      </CheckParagraph>
      <Image
        src={RiskLevelIcons[level]}
        alt={`${level} risk`}
        height="18px"
        width="18px"
      />
    </Flex>
  );
};

const Detail = ({
  label,
  value,
  wide,
}: {
  label: string;
  value: any;
  wide?: boolean;
}) => (
  <DetailItem wide={wide}>
    <DetailLabel>{label}</DetailLabel>
    <DetailValue>{getDisplayValue(value)}</DetailValue>
  </DetailItem>
);

const PartyPanelItem = ({ party, index }: { party: any; index: number }) => {
  const [expanded, setExpanded] = useState(false);
  const highestPartyRisk = getHighestPartyRisk(party);
  const addressSummary = getAddressSummary(party);
  const addressLine = getAddressLine(party);

  return (
    <PartyPanel
      key={party?.profile_id ?? party?.id ?? index}
      risk={highestPartyRisk}
      expanded={expanded}
    >
      <ExpansionPanel
        defaultExpanded={false}
        onToggle={setExpanded}
        title={
          <PanelTitleGrid>
            <Box>
              <Paragraph fontSize={1} fontWeight={700} my={0}>
                {getPartyName(party)}
              </Paragraph>
              <Paragraph color="gray.60" fontSize={1} my={0}>
                {addressSummary || getDisplayValue(party?.reference)}
              </Paragraph>
            </Box>

            <Box>
              <DetailLabel>Type</DetailLabel>
              <DetailValue>{getPartyType(party)}</DetailValue>
            </Box>

            <Box>
              <DetailLabel>Relationship</DetailLabel>
              <DetailValue>{getRelationshipType(party)}</DetailValue>
            </Box>

            <Box>
              <DetailLabel>Ownership</DetailLabel>
              <DetailValue>{getOwnership(party)}</DetailValue>
            </Box>

            <Box>
              <DetailLabel>Highest risk</DetailLabel>
              <RiskBadge risk={highestPartyRisk} />
            </Box>
          </PanelTitleGrid>
        }
      >
        <Flex flexDirection="column" gap="18">
          <DetailSection>
            <DetailSectionTitle>
              {isEntityParty(party) ? "Entity details" : "Party details"}
            </DetailSectionTitle>
            <DetailGrid>
              {isEntityParty(party) ? (
                <>
                  <Detail label="Entity type" value={getEntityType(party)} />
                  <Detail
                    label="Registration number"
                    value={party?.registration_number}
                  />
                  <Detail
                    label="Date of incorporation"
                    value={DateFormatter(party?.date_of_incorporation)}
                  />
                  <Detail label="Country" value={party?.country} />
                  <Detail
                    label="Company status"
                    value={formatStatus(party?.company_status)}
                  />
                  <Detail label="Reference" value={party?.reference} />
                </>
              ) : (
                <>
                  <Detail label="First name" value={party?.first_name} />
                  <Detail label="Middle name" value={party?.middle_name} />
                  <Detail label="Last name" value={party?.last_name} />
                  <Detail label="Email" value={party?.email} />
                  <Detail
                    label="Date of birth"
                    value={DateFormatter(party?.date_of_birth)}
                  />
                  <Detail label="Status" value={formatStatus(party?.status)} />
                </>
              )}
            </DetailGrid>
          </DetailSection>

          <DetailSection>
            <DetailSectionTitle>Relationship</DetailSectionTitle>
            <DetailGrid>
              <Detail
                label="Relationship type"
                value={getRelationshipType(party)}
              />
              <Detail
                label="Relationship percentage"
                value={getOwnership(party)}
              />
              <Detail
                label="Relationship description"
                value={party?.relationship_description}
                wide
              />
            </DetailGrid>
          </DetailSection>

          <DetailSection>
            <DetailSectionTitle>Risk</DetailSectionTitle>
            <DetailGrid>
              <DetailItem>
                <DetailLabel>Highest risk</DetailLabel>
                <RiskBadge risk={highestPartyRisk} />
              </DetailItem>
              <DetailItem>
                <DetailLabel>Entity type risk</DetailLabel>
                <RiskBadge risk={getEntityTypeRisk(party)} />
              </DetailItem>
              <DetailItem>
                <DetailLabel>Relationship risk</DetailLabel>
                <RiskBadge risk={getRelationshipRisk(party) || "low"} />
              </DetailItem>
            </DetailGrid>
          </DetailSection>

          <DetailSection>
            <DetailSectionTitle>Address</DetailSectionTitle>
            <DetailValue>{addressLine || "-"}</DetailValue>
          </DetailSection>
        </Flex>
      </ExpansionPanel>
    </PartyPanel>
  );
};

export const AssociatedPartiesCheck = (props: any) => {
  const { isOpen, onRequestClose, id, useSlider = true } = props;

  const { data: individualCheckData, isLoading: isLoadingIndividualCheck } =
    useIndividualCheck(id);

  const check = individualCheckData?.data;
  const instance: any = check?.instance;
  const parties = useMemo(() => getParties(instance), [instance]);

  const reviewParties = parties.filter(
    (party) => riskRank[getHighestPartyRisk(party)] >= riskRank.medium,
  );

  const title = check?.title || IndividualChecksMap.parties_check.title;

  const content = (
    <CheckContainer>
      <Flex justifyContent="space-between">
        <CheckTitle>{title}</CheckTitle>

        {check?.status?.status === CheckStatusEnum.Review && (
          <ManualAcceptReject title={title} id={id} isMonitoring={false} />
        )}
      </Flex>

      {check?.status?.status && <CheckResults status={check.status.status} />}

      {instance?.approval_comment && (
        <ApprovalComment
          data={instance.approval_comment}
          status={check?.status?.status}
        />
      )}

      <CheckFields
        individual_checkfield_result={check?.individual_checkfield_result}
      />

      <Section pb={3}>
        <H3 mb={3}>Associated parties risk summary</H3>

        <Flex gap="48" flexWrap="wrap">
          <div>
            <CheckHeading>Total parties</CheckHeading>
            <SummaryValue>{parties.length}</SummaryValue>
          </div>
          <div>
            <CheckHeading>Review required</CheckHeading>
            <SummaryValue>
              {reviewParties.length > 0 ? "Yes" : "No"}
            </SummaryValue>
          </div>
          <div>
            <CheckHeading>Medium / high risk parties</CheckHeading>
            <SummaryValue>{reviewParties.length}</SummaryValue>
          </div>
        </Flex>

        {check?.status?.status === CheckStatusEnum.Accepted && (
          <Paragraph color="gray.60" fontSize={1} mb={0}>
            All associated parties are low risk.
          </Paragraph>
        )}
      </Section>

      <Section>
        <H3>Submitted party details</H3>

        {parties.length === 0 ? (
          <Paragraph color="gray.60" fontSize={1}>
            No associated parties were submitted.
          </Paragraph>
        ) : (
          parties.map((party, index) => (
            <PartyPanelItem
              key={party?.profile_id ?? party?.id ?? index}
              party={party}
              index={index}
            />
          ))
        )}
      </Section>
    </CheckContainer>
  );

  return (
    <>
      {isLoadingIndividualCheck ? (
        <Flex justifyContent="center" alignItems="center" minHeight="60vh">
          <Loader />
        </Flex>
      ) : useSlider ? (
        <Slider
          isOpen={isOpen}
          onRequestClose={() => onRequestClose(false)}
          width="70%"
        >
          {content}
        </Slider>
      ) : (
        content
      )}
    </>
  );
};
